import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAccent } from '../context/AccentContext';
import { UserManagementPanel, type UserManagementPanelLabels } from 'auth-sso-kit/client';
import { Upload, Trash2, RefreshCw, Check, Minus, Shield, Database, X } from 'lucide-react';
import { DropdownSelect } from '../components/DropdownSelect';
import { useT } from '../i18n';
import type { GridColumn, GridRow, CalendarAdminConfig, RoleUser, PermissionCapability } from '../types';

type Tab = 'users' | 'roles' | 'permissions' | 'integrations' | 'naming' | 'personalisation';
const TABS: Tab[] = ['users', 'roles', 'permissions', 'integrations', 'naming', 'personalisation'];

// Reads the active tab from the hash (#admin?tab=roles) so a shared link can
// point straight at any tab, mirroring GerancePage's parseGeranceHash.
function parseAdminHash(): Tab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const tab = params.get('tab');
  return (tab && (TABS as string[]).includes(tab)) ? tab as Tab : 'users';
}

export function AdminPage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>(() => parseAdminHash());
  const { accent, setAccent, logoUrl, refreshSettings } = useAccent();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [portfolioCols, setPortfolioCols] = useState<GridColumn[]>([]);
  const [pipelineCols, setPipelineCols] = useState<GridColumn[]>([]);
  const [kanbanCols, setKanbanCols] = useState<GridColumn[]>([]);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [newPortfolioCol, setNewPortfolioCol] = useState('');
  const [newPipelineCol, setNewPipelineCol] = useState('');
  const [newKanbanCol, setNewKanbanCol] = useState('');
  const [newRow, setNewRow] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('');
  const [newPipelineColor, setNewPipelineColor] = useState('#6b7280');
  const [emailPattern, setEmailPattern] = useState('');
  const [emailPatternSaved, setEmailPatternSaved] = useState(false);

  // Dev-only: reset dev DB from prod
  const [canRefresh, setCanRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetText, setResetText] = useState('');
  // Dev-only: apply the Phase 2 stage-model migration
  const [canMigrate, setCanMigrate] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<{ stages: { label: string; sort_order: number }[]; distribution: { label: string; projects: number }[] } | null>(null);

  // Calendar integration
  const [calTenantId, setCalTenantId] = useState('');
  const [calClientId, setCalClientId] = useState('');
  const [calClientSecret, setCalClientSecret] = useState('');
  const [calWebhookUrl, setCalWebhookUrl] = useState('');
  const [calConfigSaved, setCalConfigSaved] = useState(false);
  const [calStats, setCalStats] = useState<{ configured: boolean; syncedUsers: number; activeSubs: number; totalEvents: number } | null>(null);
  const [calRematching, setCalRematching] = useState(false);
  const [calSyncing, setCalSyncing] = useState(false);

  // Slack integration
  const [slackBotToken, setSlackBotToken] = useState('');
  const [slackSigningSecret, setSlackSigningSecret] = useState('');
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackConfigSaved, setSlackConfigSaved] = useState(false);
  const [slackStats, setSlackStats] = useState<{ configured: boolean; channels_created: number; mapped_users: number } | null>(null);
  const [slackCreatingChannels, setSlackCreatingChannels] = useState(false);

  // Speakr integration
  const [speakrBaseUrl, setSpeakrBaseUrl] = useState('');
  const [speakrConfigSaved, setSpeakrConfigSaved] = useState(false);
  const [speakrStats, setSpeakrStats] = useState<{ configured: boolean; totalTranscriptions: number; configuredUsers: number } | null>(null);
  const [speakrSyncing, setSpeakrSyncing] = useState(false);
  const [speakrRematching, setSpeakrRematching] = useState(false);
  const [speakrBackfilling, setSpeakrBackfilling] = useState(false);
  const [seedingDummy, setSeedingDummy] = useState(false);

  // Pistache (AI / LLM)
  const [aiApiUrl, setAiApiUrl] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [aiApiUrlSaved, setAiApiUrlSaved] = useState(false);
  const [aiModelSaved, setAiModelSaved] = useState(false);
  const [aiModels, setAiModels] = useState<string[]>([]);
  const [aiModelsLoading, setAiModelsLoading] = useState(false);
  const [aiModelError, setAiModelError] = useState<string | null>(null);

  // RCAJO (Resource & Capability inventory)
  const [rcajoBaseUrl, setRcajoBaseUrl] = useState('');
  const [rcajoHost, setRcajoHost] = useState('');
  const [rcajoApiKey, setRcajoApiKey] = useState('');
  const [rcajoUrlSaved, setRcajoUrlSaved] = useState(false);
  const [rcajoHostSaved, setRcajoHostSaved] = useState(false);
  const [rcajoKeySaved, setRcajoKeySaved] = useState(false);
  const [rcajoPinging, setRcajoPinging] = useState(false);
  const [rcajoPingResult, setRcajoPingResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Roles
  const [roleUsers, setRoleUsers] = useState<RoleUser[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);

  // Permissions overview
  const [permissions, setPermissions] = useState<PermissionCapability[]>([]);

  const loadRoleUsers = async () => {
    setRoleLoading(true);
    try {
      const users = await api.getRoleUsers() as RoleUser[];
      setRoleUsers(users);
    } catch { /* */ }
    setRoleLoading(false);
  };

  const loadPermissions = async () => {
    try {
      const data = await api.getPermissionsOverview() as { capabilities: PermissionCapability[] };
      setPermissions(data.capabilities);
    } catch { /* */ }
  };

  // Keep the hash in sync with the open tab (replaceState, not pushState, so
  // switching tabs doesn't spam the back-stack) — the address bar is then
  // always a direct, shareable link back to the tab in view.
  useEffect(() => {
    const newHash = tab === 'users' ? '#admin' : `#admin?tab=${tab}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [tab]);

  // Lazy-load roles/permissions data for the open tab — fires both on click
  // and when a direct link (#admin?tab=roles) lands here already on that tab.
  useEffect(() => {
    if (tab === 'roles' && roleUsers.length === 0) loadRoleUsers();
    if (tab === 'permissions' && permissions.length === 0) loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      await api.updateUserRole(userId, newRole);
      setRoleUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole as RoleUser['role'], is_admin: newRole === 'admin' } : u));
    } catch (err: any) {
      alert(err.message || t('admin.roleUpdateFailedAlert'));
    }
  };

  const loadGrid = async () => {
    const [pc, plc, kc, r] = await Promise.all([
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
      api.getGridColumns('kanban') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
    ]);
    setPortfolioCols(pc);
    setPipelineCols(plc);
    setKanbanCols(kc);
    setRows(r);
  };

  useEffect(() => {
    loadGrid();
    api.getSettings().then((settings: any) => {
      if (settings.email_pattern) setEmailPattern(settings.email_pattern);
      setCanRefresh(settings.canRefreshFromProd === 'true');
      setCanMigrate(settings.canMigrateStages === 'true');
      if (settings.dev_last_refreshed_from_prod) setLastRefreshed(settings.dev_last_refreshed_from_prod);
      if (settings.ai_api_url) setAiApiUrl(settings.ai_api_url);
      if (settings.ai_model) setAiModel(settings.ai_model);
      if (settings.rcajo_base_url) setRcajoBaseUrl(settings.rcajo_base_url);
      if (settings.rcajo_host) setRcajoHost(settings.rcajo_host);
      if (settings.rcajo_api_key) setRcajoApiKey(settings.rcajo_api_key);
    }).catch(() => {});
    (api.getCalendarAdminConfig() as Promise<CalendarAdminConfig>).then(cfg => {
      setCalTenantId(cfg.msgraph_tenant_id || '');
      setCalClientId(cfg.msgraph_client_id || '');
      setCalClientSecret(cfg.msgraph_client_secret || '');
      setCalWebhookUrl(cfg.msgraph_webhook_url || '');
      setCalStats({ configured: cfg.configured, syncedUsers: cfg.syncedUsers, activeSubs: cfg.activeSubs, totalEvents: cfg.totalEvents });
    }).catch(() => {});
    (api.getSpeakrAdminConfig() as Promise<any>).then(cfg => {
      setSpeakrBaseUrl(cfg.speakr_base_url || '');
      setSpeakrStats({ configured: cfg.configured, totalTranscriptions: cfg.totalTranscriptions, configuredUsers: cfg.configuredUsers });
    }).catch(() => {});
    (api.getSlackConfig() as Promise<any>).then(cfg => {
      setSlackBotToken(cfg.bot_token || '');
      setSlackSigningSecret(cfg.signing_secret || '');
      setSlackEnabled(cfg.enabled || false);
      setSlackStats({ configured: cfg.configured, channels_created: cfg.channels_created, mapped_users: cfg.mapped_users });
    }).catch(() => {});
  }, []);

  const addColumn = async (gridType: 'portfolio' | 'pipeline' | 'kanban', label: string) => {
    if (!label.trim()) return;
    const color = gridType === 'pipeline' ? newPipelineColor : undefined;
    await api.createGridColumn(gridType, label.trim(), color);
    if (gridType === 'portfolio') setNewPortfolioCol('');
    else if (gridType === 'pipeline') { setNewPipelineCol(''); setNewPipelineColor('#6b7280'); }
    else setNewKanbanCol('');
    loadGrid();
  };

  const addRow = async () => {
    if (!newRow.trim()) return;
    await api.createGridRow(newRow.trim());
    setNewRow('');
    loadGrid();
  };

  const startEdit = (id: number, label: string, color?: string) => {
    setEditingId(id);
    setEditLabel(label);
    setEditColor(color || '');
  };

  const saveEdit = async (type: 'column' | 'row', withColor?: boolean) => {
    if (!editingId || !editLabel.trim()) return;
    if (type === 'column') {
      await api.updateGridColumn(editingId, editLabel.trim(), withColor ? editColor || undefined : undefined);
    } else {
      await api.updateGridRow(editingId, editLabel.trim());
    }
    setEditingId(null);
    setEditLabel('');
    setEditColor('');
    loadGrid();
  };

  const deleteColumn = async (id: number) => {
    try {
      await api.deleteGridColumn(id);
      loadGrid();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const deleteRow = async (id: number) => {
    try {
      await api.deleteGridRow(id);
      loadGrid();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const renderList = (items: { id: number; label: string }[], type: 'column' | 'row', onDelete: (id: number) => void) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s4)' }}>
      {items.map((item) => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', padding: 'var(--s4) var(--s8)', background: 'var(--bg)', borderRadius: 'var(--r1)' }}>
          {editingId === item.id ? (
            <>
              <input className="form-input" style={{ flex: 1 }} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEdit(type)} autoFocus />
              <button className="btn btn-ghost btn-sm" onClick={() => saveEdit(type)}>{t('common.save')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>{t('common.cancel')}</button>
            </>
          ) : (
            <>
              <span style={{ flex: 1, fontSize: 'var(--t-13)' }}>{item.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => startEdit(item.id, item.label)}>{t('common.edit')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => onDelete(item.id)}>{t('common.delete')}</button>
            </>
          )}
        </div>
      ))}
    </div>
  );

  const renderPipelineList = (items: GridColumn[], onDelete: (id: number) => void) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s4)' }}>
      {items.map((item) => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', padding: 'var(--s4) var(--s8)', background: 'var(--bg)', borderRadius: 'var(--r1)' }}>
          {editingId === item.id ? (
            <>
              <input type="color" value={editColor || '#6b7280'} onChange={(e) => setEditColor(e.target.value)} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }} />
              <input className="form-input" style={{ flex: 1 }} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEdit('column', true)} autoFocus />
              <button className="btn btn-ghost btn-sm" onClick={() => saveEdit('column', true)}>{t('common.save')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>{t('common.cancel')}</button>
            </>
          ) : (
            <>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: item.color || 'var(--ink-4)', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 'var(--t-13)' }}>{item.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => startEdit(item.id, item.label, item.color)}>{t('common.edit')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => onDelete(item.id)}>{t('common.delete')}</button>
            </>
          )}
        </div>
      ))}
    </div>
  );

  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (!file.type.includes('png')) return alert(t('admin.personalisation.logo.onlyPngAlert'));
      if (file.size > 1.5 * 1024 * 1024) return alert(t('admin.personalisation.logo.fileTooLargeAlert'));

      setUploadingLogo(true);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64 = (reader.result as string).split(',')[1];
            await fetch('/api/admin/logo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ data: base64 }),
            });
            refreshSettings();
          } catch (err) {
            console.error(err);
          } finally {
            setUploadingLogo(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error(err);
        setUploadingLogo(false);
      }
    };
    input.click();
  };

  const handleLogoRemove = async () => {
    setUploadingLogo(true);
    try {
      await fetch('/api/admin/logo', {
        method: 'DELETE',
        credentials: 'include',
      });
      refreshSettings();
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const doRefreshFromProd = async () => {
    setRefreshing(true);
    try {
      const r = await api.refreshDevFromProd() as { refreshedAt: string };
      setLastRefreshed(r.refreshedAt);
      setShowResetModal(false);
      setResetText('');
      alert(t('admin.devReset.successAlert'));
      window.location.reload();
    } catch (err: any) {
      alert(err.message || t('admin.devReset.failedAlert'));
    } finally {
      setRefreshing(false);
    }
  };

  const renderPermissionCell = (value: boolean | string) => {
    if (value === true) return <span className="permission-check"><Check size={14} /></span>;
    if (value === false) return <span className="permission-denied"><Minus size={14} /></span>;
    return <span className="permission-conditional">{value}</span>;
  };

  const tabLabels: Record<Tab, string> = {
    users: t('admin.tab.users'),
    roles: t('admin.tab.roles'),
    permissions: t('admin.tab.permissions'),
    integrations: t('admin.tab.integrations'),
    naming: t('admin.tab.naming'),
    personalisation: t('admin.tab.personalisation'),
  };

  const userManagementLabels: Partial<UserManagementPanelLabels> = {
    regModeTitle: t('admin.regModeTitle'),
    regModeOpen: t('admin.regModeOpen'),
    regModeAllowlist: t('admin.regModeAllowlist'),
    regModeOpenHelp: t('admin.regModeOpenHelp'),
    regModeAllowlistHelp: t('admin.regModeAllowlistHelp'),
    allowlistTitle: t('admin.allowlistTitle'),
    allowlistNotEnforced: t('admin.allowlistNotEnforced'),
    allowlistHelpEnforced: t('admin.allowlistHelpEnforced'),
    allowlistHelpNotEnforced: t('admin.allowlistHelpNotEnforced'),
    emailPlaceholder: t('admin.emailPlaceholder'),
    namePlaceholder: t('admin.namePlaceholder'),
    addToAllowlist: t('admin.addToAllowlist'),
    createAccount: t('admin.createAccount'),
    errValidEmailRequired: t('admin.errValidEmailRequired'),
    colEmail: t('common.email'),
    colStatus: t('common.status'),
    colAdded: t('admin.colAdded'),
    statusRegistered: t('admin.statusRegistered'),
    statusPending: t('admin.statusPending'),
    impersonate: t('admin.impersonate'),
    remove: t('common.remove'),
    confirmRemoveEmail: t('admin.confirmRemoveEmail'),
    userMgmtTitle: t('admin.userMgmtTitle'),
    colUser: t('admin.colUser'),
    colRole: t('admin.colRole'),
    colJoined: t('admin.colJoined'),
    badgeAdmin: t('admin.badgeAdmin'),
    badgeUser: t('admin.badgeUser'),
    removeAdmin: t('admin.removeAdmin'),
    makeAdmin: t('admin.makeAdmin'),
    rolesButton: t('admin.rolesButton'),
    resetPassword: t('admin.resetPassword'),
    deleteButton: t('common.delete'),
    confirmDeleteUser: t('admin.confirmDeleteUser'),
    newPasswordPlaceholder: t('admin.newPasswordPlaceholder'),
    setButton: t('admin.setButton'),
    saveRoles: t('admin.saveRoles'),
    errPasswordMin: t('admin.errPasswordMin'),
    passwordResetDone: t('admin.passwordResetDone'),
  };

  return (
    <div className="admin-page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s24)' }}>
      <div className="tabs">
        {TABS.map(tabKey => (
          <button key={tabKey} className={`tab${tab === tabKey ? ' active' : ''}`} onClick={() => setTab(tabKey)}>
            {tabLabels[tabKey]}
          </button>
        ))}
      </div>

      {/* ═══ DEV-ONLY: RESET DEV FROM PROD ═══ */}
      {canRefresh && (
        <section style={{ border: '1px solid var(--line)', borderRadius: 'var(--r2)', padding: 'var(--s16)', background: 'var(--bg-inset, var(--bg))' }}>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s8)' }}>
            <Database size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            {t('admin.devReset.title')}
          </h2>
          <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s12)' }}>
            {t('admin.devReset.descriptionPrefix')} <strong>dev</strong> {t('admin.devReset.descriptionSuffix')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s12)', flexWrap: 'wrap' }}>
            <button className="btn btn-accent btn-sm" disabled={refreshing} onClick={() => { setResetText(''); setShowResetModal(true); }}>
              <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> {t('admin.devReset.button')}
            </button>
            {lastRefreshed && (
              <span className="text-sm text-ink-3">{t('admin.devReset.lastReset', { date: new Date(lastRefreshed).toLocaleString() })}</span>
            )}
          </div>
        </section>
      )}

      {/* ═══ DEV-ONLY: APPLY STAGE-MODEL MIGRATION ═══ */}
      {canMigrate && (
        <section style={{ border: '1px solid var(--line)', borderRadius: 'var(--r2)', padding: 'var(--s16)', background: 'var(--bg-inset, var(--bg))' }}>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s8)' }}>
            <Database size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            {t('admin.migration.title')}
          </h2>
          <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s12)' }}>
            {t('admin.migration.descriptionPrefix')} <strong>dev</strong> {t('admin.migration.descriptionSuffix')}
          </p>
          <button className="btn btn-accent btn-sm" disabled={migrating} onClick={async () => {
            setMigrating(true);
            try { setMigrateResult(await api.migrateStages() as typeof migrateResult); }
            catch (e: any) { alert(t('admin.migration.failedAlert', { message: e?.message || e })); }
            finally { setMigrating(false); }
          }}>
            <RefreshCw size={14} className={migrating ? 'spin' : ''} /> {t('admin.migration.button')}
          </button>
          {migrateResult && (
            <div style={{ marginTop: 'var(--s12)' }} className="text-sm">
              <div style={{ fontWeight: 600, marginBottom: 'var(--s4)' }}>{t('admin.migration.stagesNowLabel')}</div>
              <div className="text-ink-3">{migrateResult.stages.map(s => s.label).join('  →  ')}</div>
              <div style={{ fontWeight: 600, margin: 'var(--s8) 0 var(--s4)' }}>{t('admin.migration.projectsPerStageLabel')}</div>
              {migrateResult.distribution.filter(d => d.projects > 0).length === 0 ? (
                <div className="text-ink-3">{t('admin.migration.noProjects')}</div>
              ) : (
                migrateResult.distribution.filter(d => d.projects > 0).map(d => (
                  <div key={d.label} className="text-ink-3">{d.label}: {d.projects}</div>
                ))
              )}
            </div>
          )}
        </section>
      )}

      {/* ═══ USERS TAB ═══ */}
      {tab === 'users' && (
        <section>
          <UserManagementPanel
            labels={userManagementLabels}
            extraColumns={[{
              header: t('admin.lastLogin'),
              render: (u: any) => u.lastLogin
                ? new Date(u.lastLogin + 'Z').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                : t('admin.never'),
            }]}
          />
        </section>
      )}

      {/* ═══ ROLES TAB ═══ */}
      {tab === 'roles' && (
        <section>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>
            <Shield size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            {t('admin.roles.title')}
          </h2>
          <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s16)' }}>
            {t('admin.roles.description')}
          </p>

          {roleLoading ? (
            <div className="spinner" />
          ) : (
            <table className="permission-matrix">
              <thead>
                <tr>
                  <th>{t('admin.colUser')}</th>
                  <th>{t('common.email')}</th>
                  <th>{t('admin.colRole')}</th>
                  <th>{t('admin.lastLogin')}</th>
                </tr>
              </thead>
              <tbody>
                {roleUsers.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.name}</td>
                    <td style={{ color: 'var(--ink-3)', fontSize: 'var(--t-12)' }}>{u.email}</td>
                    <td style={{ minWidth: 140 }}>
                      <DropdownSelect
                        value={u.role}
                        options={[
                          { value: 'admin', label: t('admin.role.admin') },
                          { value: 'direction', label: t('admin.role.direction') },
                          { value: 'user', label: t('admin.role.user') },
                        ]}
                        onChange={(val) => handleRoleChange(u.id, String(val))}
                        placeholder={t('admin.selectRolePlaceholder')}
                      />
                    </td>
                    <td style={{ color: 'var(--ink-3)', fontSize: 'var(--t-12)' }}>
                      {u.last_login
                        ? new Date(u.last_login + 'Z').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                        : t('admin.never')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ═══ PERMISSIONS TAB ═══ */}
      {tab === 'permissions' && (
        <section>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>
            {t('admin.permissions.title')}
          </h2>
          <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s16)' }}>
            {t('admin.permissions.description')}
          </p>

          <table className="permission-matrix">
            <thead>
              <tr>
                <th style={{ width: '40%' }}>{t('admin.permissions.colCapability')}</th>
                <th>{t('admin.role.admin')}</th>
                <th>{t('admin.role.direction')}</th>
                <th>{t('admin.role.user')}</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((cap, i) => (
                <tr key={i}>
                  <td>{cap.name}</td>
                  <td>{renderPermissionCell(cap.admin)}</td>
                  <td>{renderPermissionCell(cap.direction)}</td>
                  <td>{renderPermissionCell(cap.user)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* ═══ INTEGRATIONS TAB ═══ */}
      {tab === 'integrations' && (
        <>
          {/* Calendar Integration */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.integrations.calendar.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.integrations.calendar.description')}{' '}
              {t('admin.integrations.calendar.registerPrefix')} <strong>Calendars.Read</strong> {t('admin.integrations.calendar.registerSuffix')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)', maxWidth: 480 }}>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.calendar.tenantId')}</label>
                <input className="form-input" value={calTenantId} onChange={(e) => { setCalTenantId(e.target.value); setCalConfigSaved(false); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.calendar.clientId')}</label>
                <input className="form-input" value={calClientId} onChange={(e) => { setCalClientId(e.target.value); setCalConfigSaved(false); }} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.calendar.clientSecret')}</label>
                <input className="form-input" type="password" value={calClientSecret} onChange={(e) => { setCalClientSecret(e.target.value); setCalConfigSaved(false); }} placeholder={t('admin.integrations.calendar.clientSecretPlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.calendar.webhookUrl')}</label>
                <input className="form-input" value={calWebhookUrl} onChange={(e) => { setCalWebhookUrl(e.target.value); setCalConfigSaved(false); }} placeholder="https://pmo.example.com/api/calendar/webhook" />
                <p className="text-sm text-ink-3" style={{ marginTop: 4 }}>
                  {t('admin.integrations.calendar.webhookHelp')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.saveCalendarConfig({
                      tenant_id: calTenantId,
                      client_id: calClientId,
                      client_secret: calClientSecret,
                      webhook_url: calWebhookUrl,
                    });
                    setCalConfigSaved(true);
                    setTimeout(() => setCalConfigSaved(false), 2000);
                    (api.getCalendarAdminConfig() as Promise<CalendarAdminConfig>).then(cfg => {
                      setCalStats({ configured: cfg.configured, syncedUsers: cfg.syncedUsers, activeSubs: cfg.activeSubs, totalEvents: cfg.totalEvents });
                    }).catch(() => {});
                  }}
                >
                  {calConfigSaved ? t('common.saved') : t('common.save')}
                </button>
                {calStats?.configured && <span className="badge badge-ok">{t('admin.integrations.connected')}</span>}
              </div>
            </div>

            {calStats?.configured && (
              <div style={{ marginTop: 'var(--s16)' }}>
                <div className="detail-row-label" style={{ marginBottom: 'var(--s8)' }}>{t('admin.integrations.calendar.syncStatusLabel')}</div>
                <div style={{ fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                  {t('admin.integrations.calendar.syncStatusText', { syncedUsers: calStats.syncedUsers, activeSubs: calStats.activeSubs, totalEvents: calStats.totalEvents })}
                </div>
                <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s8)', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={calSyncing}
                    onClick={async () => {
                      setCalSyncing(true);
                      try {
                        const result = await api.forceFullSync() as { usersSynced: number; totalEvents: number };
                        alert(t('admin.integrations.calendar.syncedAlert', { totalEvents: result.totalEvents, usersSynced: result.usersSynced }));
                        (api.getCalendarAdminConfig() as Promise<CalendarAdminConfig>).then(cfg => {
                          setCalStats({ configured: cfg.configured, syncedUsers: cfg.syncedUsers, activeSubs: cfg.activeSubs, totalEvents: cfg.totalEvents });
                        }).catch(() => {});
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setCalSyncing(false);
                      }
                    }}
                  >
                    {calSyncing ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.calendar.forceFullSync')}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={calRematching}
                    onClick={async () => {
                      setCalRematching(true);
                      try {
                        const result = await api.forceRematch() as { eventsRematched: number };
                        alert(t('admin.integrations.calendar.rematchedAlert', { count: result.eventsRematched }));
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setCalRematching(false);
                      }
                    }}
                  >
                    {calRematching ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.calendar.forceRematchAll')}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Speakr Integration */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.integrations.speakr.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.integrations.speakr.description')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)', maxWidth: 480 }}>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.speakr.baseUrl')}</label>
                <input className="form-input" value={speakrBaseUrl} onChange={(e) => { setSpeakrBaseUrl(e.target.value); setSpeakrConfigSaved(false); }} placeholder="http://192.168.80.214" />
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.saveSpeakrAdminConfig(speakrBaseUrl);
                    setSpeakrConfigSaved(true);
                    setTimeout(() => setSpeakrConfigSaved(false), 2000);
                    (api.getSpeakrAdminConfig() as Promise<any>).then(cfg => {
                      setSpeakrStats({ configured: cfg.configured, totalTranscriptions: cfg.totalTranscriptions, configuredUsers: cfg.configuredUsers });
                    }).catch(() => {});
                  }}
                >
                  {speakrConfigSaved ? t('common.saved') : t('common.save')}
                </button>
                {speakrStats?.configured && <span className="badge badge-ok">{t('admin.integrations.configured')}</span>}
              </div>
            </div>

            {speakrStats && (
              <div style={{ marginTop: 'var(--s16)' }}>
                <div className="detail-row-label" style={{ marginBottom: 'var(--s8)' }}>{t('admin.integrations.statusLabel')}</div>
                <div style={{ fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                  {t('admin.integrations.speakr.statusText', { configuredUsers: speakrStats.configuredUsers, totalTranscriptions: speakrStats.totalTranscriptions })}
                </div>

                <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s12)', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={speakrSyncing}
                    onClick={async () => {
                      setSpeakrSyncing(true);
                      try {
                        const result = await api.triggerSpeakrSyncAll() as any;
                        alert(t('admin.integrations.speakr.syncedAlert', { totalSynced: result.totalSynced ?? 0, usersSynced: result.usersSynced ?? 0 }));
                        (api.getSpeakrAdminConfig() as Promise<any>).then(cfg => {
                          setSpeakrStats({ configured: cfg.configured, totalTranscriptions: cfg.totalTranscriptions, configuredUsers: cfg.configuredUsers });
                        }).catch(() => {});
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setSpeakrSyncing(false);
                      }
                    }}
                  >
                    {speakrSyncing ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.speakr.syncAllUsers')}
                  </button>

                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={speakrRematching}
                    onClick={async () => {
                      setSpeakrRematching(true);
                      try {
                        const result = await api.forceSpeakrRematch() as any;
                        alert(t('admin.integrations.speakr.rematchedAlert', { count: result.rematched ?? 0 }));
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setSpeakrRematching(false);
                      }
                    }}
                  >
                    {speakrRematching ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.speakr.rematchTranscriptions')}
                  </button>

                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={speakrBackfilling}
                    onClick={async () => {
                      setSpeakrBackfilling(true);
                      try {
                        const result = await api.backfillTranscripts() as any;
                        alert(t('admin.integrations.speakr.backfilledAlert', { count: result.filled ?? 0 }));
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setSpeakrBackfilling(false);
                      }
                    }}
                  >
                    {speakrBackfilling ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.speakr.backfillTranscripts')}
                  </button>
                </div>
              </div>
            )}

            {/* Dummy calendar data */}
            <div style={{ marginTop: 'var(--s24)', borderTop: '1px solid var(--line)', paddingTop: 'var(--s16)' }}>
              <div className="detail-row-label" style={{ marginBottom: 'var(--s8)' }}>{t('admin.integrations.speakr.testDataLabel')}</div>
              <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
                {t('admin.integrations.speakr.testDataDescription')}
              </p>
              <div style={{ display: 'flex', gap: 'var(--s8)' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={seedingDummy}
                  onClick={async () => {
                    setSeedingDummy(true);
                    try {
                      const result = await api.seedDummyEvents() as any;
                      alert(t('admin.integrations.speakr.seededAlert', { count: result.created ?? 0 }));
                    } catch (err: any) {
                      alert(err.message);
                    } finally {
                      setSeedingDummy(false);
                    }
                  }}
                >
                  {t('admin.integrations.speakr.seedDummyEvents')}
                </button>
                <button
                  className="btn btn-ghost btn-sm text-err"
                  onClick={async () => {
                    try {
                      const result = await api.deleteDummyEvents() as any;
                      alert(t('admin.integrations.speakr.deletedAlert', { count: result.deleted ?? 0 }));
                    } catch (err: any) {
                      alert(err.message);
                    }
                  }}
                >
                  <Trash2 size={14} /> {t('admin.integrations.speakr.clearDummyEvents')}
                </button>
              </div>
            </div>
          </section>

          {/* Slack Integration */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.integrations.slack.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.integrations.slack.description')}{' '}
              {t('admin.integrations.slack.scopesPrefix')} <strong>groups:write</strong>, <strong>groups:read</strong>, <strong>chat:write</strong>, <strong>commands</strong>, <strong>users:read</strong>, <strong>users:read.email</strong>.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)', maxWidth: 480 }}>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.slack.botToken')}</label>
                <input className="form-input" type="password" value={slackBotToken} onChange={(e) => { setSlackBotToken(e.target.value); setSlackConfigSaved(false); }} placeholder="xoxb-..." />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.slack.signingSecret')}</label>
                <input className="form-input" type="password" value={slackSigningSecret} onChange={(e) => { setSlackSigningSecret(e.target.value); setSlackConfigSaved(false); }} placeholder={t('admin.integrations.slack.signingSecretPlaceholder')} />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={slackEnabled} onChange={(e) => { setSlackEnabled(e.target.checked); setSlackConfigSaved(false); }} />
                  <span className="form-label" style={{ margin: 0 }}>{t('admin.integrations.slack.enableForNewProjects')}</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.saveSlackConfig({
                      bot_token: slackBotToken,
                      signing_secret: slackSigningSecret,
                      enabled: slackEnabled,
                    });
                    setSlackConfigSaved(true);
                    setTimeout(() => setSlackConfigSaved(false), 2000);
                    (api.getSlackConfig() as Promise<any>).then(cfg => {
                      setSlackStats({ configured: cfg.configured, channels_created: cfg.channels_created, mapped_users: cfg.mapped_users });
                    }).catch(() => {});
                  }}
                >
                  {slackConfigSaved ? t('common.saved') : t('common.save')}
                </button>
                {slackStats?.configured && <span className="badge badge-ok">{t('admin.integrations.configured')}</span>}
              </div>
            </div>

            {slackStats?.configured && (
              <div style={{ marginTop: 'var(--s16)' }}>
                <div className="detail-row-label" style={{ marginBottom: 'var(--s8)' }}>{t('admin.integrations.statusLabel')}</div>
                <div style={{ fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                  {t('admin.integrations.slack.statusText', { channelsCreated: slackStats.channels_created, mappedUsers: slackStats.mapped_users })}
                </div>
                <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s12)', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={slackCreatingChannels}
                    onClick={async () => {
                      setSlackCreatingChannels(true);
                      try {
                        const result = await api.slackCreateAllChannels() as any;
                        alert(t('admin.integrations.slack.createChannelsAlert', { created: result.created ?? 0, failed: result.failed ?? 0, total: result.total ?? 0 }));
                        (api.getSlackConfig() as Promise<any>).then(cfg => {
                          setSlackStats({ configured: cfg.configured, channels_created: cfg.channels_created, mapped_users: cfg.mapped_users });
                        }).catch(() => {});
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setSlackCreatingChannels(false);
                      }
                    }}
                  >
                    {slackCreatingChannels ? <RefreshCw size={14} className="spin" /> : null}
                    {' '}{t('admin.integrations.slack.createChannelsButton')}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Pistache (AI / LLM) */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.integrations.pistache.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.integrations.pistache.description')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)', maxWidth: 480 }}>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.pistache.apiUrl')}</label>
                <input className="form-input" value={aiApiUrl} onChange={(e) => { setAiApiUrl(e.target.value); setAiApiUrlSaved(false); }} placeholder="https://api.example.com/v1" />
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.updateSetting('ai_api_url', aiApiUrl);
                    setAiApiUrlSaved(true);
                    setTimeout(() => setAiApiUrlSaved(false), 2000);
                  }}
                >
                  {aiApiUrlSaved ? t('common.saved') : t('common.save')}
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">{t('admin.integrations.pistache.model')}</label>
                <input
                  className="form-input"
                  value={aiModel}
                  onChange={(e) => { setAiModel(e.target.value); setAiModelSaved(false); }}
                  placeholder={t('admin.integrations.pistache.modelPlaceholder')}
                  list="ai-model-list"
                />
                <datalist id="ai-model-list">
                  {aiModels.map((m) => <option key={m} value={m} />)}
                </datalist>
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.updateSetting('ai_model', aiModel);
                    setAiModelSaved(true);
                    setTimeout(() => setAiModelSaved(false), 2000);
                  }}
                >
                  {aiModelSaved ? t('common.saved') : t('common.save')}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={aiModelsLoading}
                  onClick={async () => {
                    setAiModelsLoading(true);
                    setAiModelError(null);
                    try {
                      const r = await api.getAiModels() as { models?: string[]; error?: string };
                      if (r.error) { setAiModelError(r.error); }
                      else { setAiModels(r.models || []); }
                    } catch (e: any) {
                      setAiModelError(e?.message || t('admin.integrations.pistache.fetchModelsFailed'));
                    } finally {
                      setAiModelsLoading(false);
                    }
                  }}
                >
                  {aiModelsLoading ? <RefreshCw size={14} className="spin" /> : null}
                  {' '}{t('admin.integrations.pistache.testRefreshModels')}
                </button>
                {aiModelError && <span className="text-sm" style={{ color: 'var(--err)' }}>{aiModelError}</span>}
              </div>
              {aiModels.length > 0 && (
                <div className="text-sm text-ink-3" style={{ marginTop: 'var(--s4)' }}>
                  {t('admin.integrations.pistache.availableModels', { models: aiModels.join(', ') })}
                </div>
              )}
            </div>
          </section>

          {/* RCAJO (Resource & Capability inventory) */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.integrations.rcajo.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.integrations.rcajo.description')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)', maxWidth: 480 }}>
              <div className="form-group">
                <label className="form-label">{t('admin.integrations.baseUrlLabel')}</label>
                <input className="form-input" value={rcajoBaseUrl} onChange={(e) => { setRcajoBaseUrl(e.target.value); setRcajoUrlSaved(false); }} placeholder="https://rcajo.example.com" />
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.updateSetting('rcajo_base_url', rcajoBaseUrl);
                    setRcajoUrlSaved(true);
                    setTimeout(() => setRcajoUrlSaved(false), 2000);
                  }}
                >
                  {rcajoUrlSaved ? t('common.saved') : t('common.save')}
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">{t('admin.integrations.rcajo.hostHeader')}</label>
                <input className="form-input" value={rcajoHost} onChange={(e) => { setRcajoHost(e.target.value); setRcajoHostSaved(false); }} placeholder="rcajo.example.com" />
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.updateSetting('rcajo_host', rcajoHost);
                    setRcajoHostSaved(true);
                    setTimeout(() => setRcajoHostSaved(false), 2000);
                  }}
                >
                  {rcajoHostSaved ? t('common.saved') : t('common.save')}
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">{t('admin.integrations.rcajo.apiKey')}</label>
                <input className="form-input" type="password" value={rcajoApiKey} onChange={(e) => { setRcajoApiKey(e.target.value); setRcajoKeySaved(false); }} placeholder={t('admin.integrations.rcajo.apiKeyPlaceholder')} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-accent btn-sm"
                  onClick={async () => {
                    await api.updateSetting('rcajo_api_key', rcajoApiKey);
                    setRcajoKeySaved(true);
                    setTimeout(() => setRcajoKeySaved(false), 2000);
                  }}
                >
                  {rcajoKeySaved ? t('common.saved') : t('common.save')}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={rcajoPinging}
                  onClick={async () => {
                    setRcajoPinging(true);
                    setRcajoPingResult(null);
                    try {
                      const r = await api.pingRcajo() as any;
                      if (r.ok) {
                        setRcajoPingResult({ ok: true, text: t('admin.integrations.rcajo.connectedText', { resourceCount: r.resourceCount ?? 0, capabilityCount: r.capabilityCount ?? 0 }) });
                      } else {
                        setRcajoPingResult({ ok: false, text: r.error || t('admin.integrations.rcajo.connectionFailed') });
                      }
                    } catch (e: any) {
                      setRcajoPingResult({ ok: false, text: e?.message || t('admin.integrations.rcajo.connectionFailed') });
                    } finally {
                      setRcajoPinging(false);
                    }
                  }}
                >
                  {rcajoPinging ? <RefreshCw size={14} className="spin" /> : null}
                  {' '}{t('admin.integrations.rcajo.testConnection')}
                </button>
                {rcajoPingResult && (
                  <span
                    className={`badge${rcajoPingResult.ok ? ' badge-ok' : ''}`}
                    style={!rcajoPingResult.ok ? { background: 'var(--err)', color: '#fff' } : undefined}
                  >
                    {rcajoPingResult.text}
                  </span>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ═══ NAMING TAB ═══ */}
      {tab === 'naming' && (
        <>
          {/* Email Pattern */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.naming.emailPattern.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.naming.emailPattern.description')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s12)' }}>
              <input
                className="form-input"
                value={emailPattern}
                onChange={(e) => { setEmailPattern(e.target.value); setEmailPatternSaved(false); }}
                placeholder="{firstname}.{lastname}@example.com"
                style={{ maxWidth: 360 }}
              />
              <button
                className="btn btn-accent btn-sm"
                onClick={async () => {
                  await api.updateSetting('email_pattern', emailPattern);
                  setEmailPatternSaved(true);
                  setTimeout(() => setEmailPatternSaved(false), 2000);
                }}
              >
                {emailPatternSaved ? t('common.saved') : t('common.save')}
              </button>
            </div>
          </section>

          {/* Portfolio Columns */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.naming.portfolioDomains.title')}</h2>
            {renderList(portfolioCols, 'column', deleteColumn)}
            <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s8)' }}>
              <input className="form-input" placeholder={t('admin.naming.portfolioDomains.placeholder')} value={newPortfolioCol} onChange={(e) => setNewPortfolioCol(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addColumn('portfolio', newPortfolioCol)} />
              <button className="btn btn-accent btn-sm" onClick={() => addColumn('portfolio', newPortfolioCol)}>{t('common.add')}</button>
            </div>
          </section>

          {/* Pipeline Columns */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.naming.pipelineStages.title')}</h2>
            {renderPipelineList(pipelineCols, deleteColumn)}
            <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s8)', alignItems: 'center' }}>
              <input type="color" value={newPipelineColor} onChange={(e) => setNewPipelineColor(e.target.value)} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }} />
              <input className="form-input" placeholder={t('admin.naming.pipelineStages.placeholder')} value={newPipelineCol} onChange={(e) => setNewPipelineCol(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addColumn('pipeline', newPipelineCol)} style={{ flex: 1 }} />
              <button className="btn btn-accent btn-sm" onClick={() => addColumn('pipeline', newPipelineCol)}>{t('common.add')}</button>
            </div>
          </section>

          {/* Task Board Columns */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.naming.taskBoardColumns.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.naming.taskBoardColumns.description')}
            </p>
            {renderList(kanbanCols, 'column', deleteColumn)}
            <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s8)' }}>
              <input className="form-input" placeholder={t('admin.naming.taskBoardColumns.placeholder')} value={newKanbanCol} onChange={(e) => setNewKanbanCol(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addColumn('kanban', newKanbanCol)} />
              <button className="btn btn-accent btn-sm" onClick={() => addColumn('kanban', newKanbanCol)}>{t('common.add')}</button>
            </div>
          </section>

          {/* Rows */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.naming.levels.title')}</h2>
            {renderList(rows, 'row', deleteRow)}
            <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s8)' }}>
              <input className="form-input" placeholder={t('admin.naming.levels.placeholder')} value={newRow} onChange={(e) => setNewRow(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addRow()} />
              <button className="btn btn-accent btn-sm" onClick={addRow}>{t('common.add')}</button>
            </div>
          </section>
        </>
      )}

      {/* ═══ PERSONALISATION TAB ═══ */}
      {tab === 'personalisation' && (
        <>
          {/* Accent Color */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.personalisation.accentColor.title')}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s12)' }}>
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }} />
              <span className="mono" style={{ fontSize: 'var(--t-13)' }}>{accent}</span>
            </div>
          </section>

          {/* Logo */}
          <section>
            <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('admin.personalisation.logo.title')}</h2>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s8)' }}>
              {t('admin.personalisation.logo.description')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s12)' }}>
              {logoUrl ? (
                <>
                  <div style={{
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r2)',
                    padding: 'var(--s8)',
                    background: 'var(--bg-inset)',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}>
                    <img src={logoUrl} alt={t('admin.personalisation.logo.currentLogoAlt')} style={{ height: 32 }} />
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={handleLogoUpload} disabled={uploadingLogo}>
                    {t('admin.personalisation.logo.replace')}
                  </button>
                  <button className="btn btn-ghost btn-sm text-err" onClick={handleLogoRemove} disabled={uploadingLogo}>
                    <Trash2 className="icon-sm" /> {t('common.remove')}
                  </button>
                </>
              ) : (
                <button className="btn btn-ghost" onClick={handleLogoUpload} disabled={uploadingLogo}>
                  <Upload className="icon-sm" /> {t('admin.personalisation.logo.upload')}
                </button>
              )}
            </div>
          </section>
        </>
      )}

      {/* ═══ RESET-FROM-PROD CONFIRM MODAL ═══ */}
      {showResetModal && (
        <div className="modal-overlay" onClick={() => !refreshing && setShowResetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admin.resetModal.title')}</h3>
              <button className="close-btn" onClick={() => !refreshing && setShowResetModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body flex flex-col gap-12">
              <p className="text-sm">
                {t('admin.devReset.modalWarningPart1')} <strong>{t('admin.devReset.modalWarningStrong1')}</strong> {t('admin.devReset.modalWarningPart2')}
                <strong> {t('admin.devReset.modalWarningStrong2')}</strong>{t('admin.devReset.modalWarningPart3')}
              </p>
              <div className="form-group">
                <label className="form-label">{t('admin.devReset.typePrefix')} <strong>RESET</strong> {t('admin.devReset.typeSuffix')}</label>
                <input className="form-input" value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" disabled={refreshing} onClick={() => setShowResetModal(false)}>{t('common.cancel')}</button>
              <button className="btn btn-accent" disabled={resetText !== 'RESET' || refreshing} onClick={doRefreshFromProd}>
                {refreshing ? <><RefreshCw size={14} className="spin" /> {t('admin.devReset.resettingLabel')}</> : t('admin.devReset.confirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
